import { useState, type FormEvent } from 'react';
import type { Project } from '@hangar/shared';
import { toast } from 'sonner';
import { useProjectMutations } from '@/api/hooks';
import { isEmptyBody, shownError } from '@/lib/formModel';
import { useEditFormState } from '@/lib/useFormState';
import { buildEditProjectBody, editProjectValuesOf, validateEditProject, type EditProjectField } from './projectFormModel';

const STALE_PROJECT_MESSAGE = 'Someone changed this project since you opened it. Close this dialog, reopen it and save again.';

/** Everything the Edit project form needs: its values, messages, the save handler and the problem alert. */
export function useEditProjectForm(project: Project, onDone: () => void) {
  const { update } = useProjectMutations();
  const state = useEditFormState(STALE_PROJECT_MESSAGE, 'name');
  const [values, setValues] = useState(() => editProjectValuesOf(project));
  const local = validateEditProject(values);

  /** Saves the changed fields; nothing changed closes the dialog without a request. */
  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    state.begin();
    if (local.name || local.description) return;
    const body = buildEditProjectBody(project, values);
    if (isEmptyBody(body)) return onDone();
    try {
      const saved = await update.mutateAsync({ projectId: project.id, body, etag: project.etag });
      toast.success(`${saved.name} saved`);
      onDone();
    } catch (err) {
      state.fail(err);
    }
  };

  return {
    values,
    problem: state.problem,
    pending: update.isPending,
    errorFor: (field: EditProjectField) => shownError(field, local, state.server, state.submitted),
    set: (field: EditProjectField, value: string) => setValues((v) => ({ ...v, [field]: value })),
    onSubmit,
  };
}
